import { z } from "zod";
import { zGuard } from "../zod/utils.js";

import { getResolvedWeekStart } from "../chart/chartTimeUnitUtils.js";
import {
  ChartAxisGrid,
  ChartAxisReferenceLine,
  ChartAxisTicks,
  ChartColorHex,
  ChartColorMappings,
  ChartColorScheme,
  ChartDataLabels,
  ChartDataTypeLiteral,
  ChartGeneralSettings,
  ChartInterpolate,
  ChartLineStroke,
  ChartNumberAxisScale,
  ChartOpacitiesBySeriesValues,
  ChartSeriesTypeLiteral,
  ChartShape,
  ChartTimeUnitConfig,
  ChartTypeSelectorOption,
} from "../chart/types.js";
import {
  DateColumnDisplayFormat,
  DatetimeColumnDisplayFormat,
  NumberColumnDisplayFormat,
} from "../display-table/columnDisplayFormatTypes.js";
import {
  CalciteType,
  CalciteTypeLiteral,
  HqlAggregationFunctionLiteral,
  HqlTruncUnitLiteral,
} from "../hql/types.js";
import {
  DataSourceColumnId,
  DataSourceTableId,
  SemanticDatasetName,
} from "../idTypeBrands.js";
import { getNormalEnum } from "../pseudoEnums.js";

import { ExploreCustomTimeUnit } from "./exploreCustomTimeUnit.js";
import {
  ExploreLumpOrderDirectionLiteral,
  ExploreLumpWindowFunctionLiteral,
  ExploreRankPredicate,
} from "./exploreLumpTypes.js";

export const ExploreFieldId = z.string().brand("ExploreFieldId");
export type ExploreFieldId = z.infer<typeof ExploreFieldId>;

export const ExploreAxisId = z.string().brand("ExploreAxisId");
export type ExploreAxisId = z.infer<typeof ExploreAxisId>;

export const ExploreSeriesId = z.string().brand("ExploreSeriesId");
export type ExploreSeriesId = z.infer<typeof ExploreSeriesId>;

export const ChartExploreChannelTopLevel = z.enum([
  "base-axis", // alias: x-axis
  "h-facet",
  "v-facet",
]);
export type ChartExploreChannelTopLevel = z.infer<
  typeof ChartExploreChannelTopLevel
>;

export const ChartExploreChannelOnePerSeries = z.enum([
  "color", // reused by sankey
  "opacity",
]);
export type ChartExploreChannelOnePerSeries = z.infer<
  typeof ChartExploreChannelOnePerSeries
>;

export const ChartExploreChannelManyPerSeries = z.enum([
  "cross-axis", // alias: y-axis
  "tooltip", // reused by sankey
]);
export type ChartExploreChannelManyPerSeries = z.infer<
  typeof ChartExploreChannelManyPerSeries
>;

export const ChartExploreChannel = z.enum([
  ...ChartExploreChannelTopLevel.options,
  ...ChartExploreChannelOnePerSeries.options,
  ...ChartExploreChannelManyPerSeries.options,
]);
export type ChartExploreChannel = z.infer<typeof ChartExploreChannel>;

export const PivotExploreChannel = z.enum([
  "row",
  "column", // reused by column sankey
  "value", // reused by column sankey
]);
export type PivotExploreChannel = z.infer<typeof PivotExploreChannel>;

export const NetworkSankeyExploreChannel = z.enum(["source", "destination"]);
export type NetworkSankeyExploreChannel = z.infer<
  typeof NetworkSankeyExploreChannel
>;

export const ExploreChannel = z.enum([
  ...ChartExploreChannel.options,
  ...PivotExploreChannel.options,
  ...NetworkSankeyExploreChannel.options,
]);
export type ExploreChannel = z.infer<typeof ExploreChannel>;

export const ReorderableExploreChannel = z.enum([
  ...ChartExploreChannelManyPerSeries.options,
  ...PivotExploreChannel.options,
]);
export type ReorderableExploreChannel = z.infer<
  typeof ReorderableExploreChannel
>;

// Table channel are "runtime types" as in it's a special type of types thats
// evaluated for the table at runtime but not actually stored in the spec.
// Measures and fields with aggregations are considered aggregationss and rest are groupbys.
export const ExploreTableChannel = z.enum(["aggregation", "groupby"]);
export type ExploreTableChannel = z.infer<typeof ExploreTableChannel>;

// values modelled here should be chart-specific/non-channel-specific config
// that move with the field, and are not channel-specific
// i.e. they make sense for color and x and y etc but not for the pivot table
export const ExploreAxis = z.object({
  scale: ChartNumberAxisScale.optional(),
  position: z.enum(["top", "bottom", "left", "right"]).optional(),
  labelAngle: z.number().optional(),
  maxTickLabelLimit: z.number().optional(),
  grid: ChartAxisGrid.optional(),
  ticks: ChartAxisTicks.optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  zero: z.boolean().optional(),
  colorScheme: ChartColorScheme.optional(),
  reverseColorScheme: z.boolean().optional(),
  referenceLines: z.array(ChartAxisReferenceLine).optional(),
  multiFieldTitle: z.string().optional(),
  hideTitle: z.boolean().optional(),
});
export type ExploreAxis = z.infer<typeof ExploreAxis>;

// values modelled in *ChannelConfig are series-wide values which should not
// change if a given field is moved to a different channel

export const ExploreOpacitySource = z.enum([
  "opacity", // field data if present else staticValue
  "color", // opacityBySeriesValues
  "base-axis", // opacityBySeriesValues
]);
export type ExploreOpacitySource = z.infer<typeof ExploreOpacitySource>;

export const ExploreOpacityChannelConfig = z.object({
  source: ExploreOpacitySource,
  staticValue: z.number().optional(),

  // resolve ambiguity when source='opacity' but no field is present
  // true means use the staticValue, false means render dropdown in Style tab
  staticMode: z.boolean().optional(),
});
export type ExploreOpacityChannelConfig = z.infer<
  typeof ExploreOpacityChannelConfig
>;

export const ExploreColorSource = z.enum([
  "color", // field data if present else staticValue
  "base-axis", // colorBySeriesValues
]);
export type ExploreColorSource = z.infer<typeof ExploreColorSource>;

export const ExploreColorChannelConfig = z.object({
  source: ExploreColorSource,
  staticValue: ChartColorHex.optional(),
});
export type ExploreColorChannelConfig = z.infer<
  typeof ExploreColorChannelConfig
>;

export const ExploreTextChannelConfig = z.object({
  source: z.enum(["cross-axis"]),
  dataLabels: ChartDataLabels.optional(),
  totalDataLabels: ChartDataLabels.optional(),
});
export type ExploreTextChannelConfig = z.infer<typeof ExploreTextChannelConfig>;

export const ExploreTooltipChannelConfig = z.object({
  includeAuto: z.boolean(),
});
export type ExploreTooltipChannelConfig = z.infer<
  typeof ExploreTooltipChannelConfig
>;

export const ExploreLineStrokeChannelConfig = z.object({
  staticValue: ChartLineStroke.optional(),
});
export type ExploreLineStrokeChannelConfig = z.infer<
  typeof ExploreLineStrokeChannelConfig
>;

export const ExploreLineWidthChannelConfig = z.object({
  staticValue: z.number().optional(),
});
export type ExploreLineWidthChannelConfig = z.infer<
  typeof ExploreLineWidthChannelConfig
>;

export const ExploreRadiusChannelConfig = z.object({
  staticValue: z.number().optional(),
});
export type ExploreRadiusChannelConfig = z.infer<
  typeof ExploreRadiusChannelConfig
>;

export const ExplorePointSizeChannelConfig = z.object({
  staticValue: z.number().optional(),
});
export type ExplorePointSizeChannelConfig = z.infer<
  typeof ExplorePointSizeChannelConfig
>;

export const ExplorePointShapeChannelConfig = z.object({
  staticValue: ChartShape.optional(),
});
export type ExplorePointShapeChannelConfig = z.infer<
  typeof ExplorePointShapeChannelConfig
>;

export const ExploreBin = z.object({
  size: z.number().optional(),
  count: z.number().optional(),
  useColumn: z.boolean().optional(),
});
export type ExploreBin = z.infer<typeof ExploreBin>;

export const ExploreFacet = z.object({
  columns: z.number().optional(),
  sharedY: z.boolean().optional(),
});
export type ExploreFacet = z.infer<typeof ExploreFacet>;

export const ExploreFieldTypeLiteral = z.enum([
  // This is a dimension from a semantic dataset
  "DIMENSION",
  // This is a measure from a semantic dataset
  "MEASURE",
  // This is a column on the base warehouse table/dataframe
  "COLUMN",
]);
export type ExploreFieldType = z.infer<typeof ExploreFieldTypeLiteral>;
export const ExploreFieldType = getNormalEnum(ExploreFieldTypeLiteral);

export const ExploreMeasureOrderFilter = z.object({
  dimensionId: ExploreFieldId,
  value: z.union([z.string(), z.number(), z.boolean()]),
});
export type ExploreMeasureOrderFilter = z.infer<
  typeof ExploreMeasureOrderFilter
>;

export const ExploreMeasureOrder = z.object({
  measureId: ExploreFieldId,
  filter: z.array(ExploreMeasureOrderFilter).optional(),
});
export type ExploreMeasureOrder = z.infer<typeof ExploreMeasureOrder>;

export const ExploreSortModeLiteral = z.enum([
  "value-ascending",
  "value-descending",
  "cross-axis-ascending",
  "cross-axis-descending",
  "custom-order",
  "measure-ascending",
  "measure-descending",
]);
export type ExploreSortMode = z.infer<typeof ExploreSortModeLiteral>;

export const ExploreSort = z.object({
  mode: ExploreSortModeLiteral,
  customOrder: z.array(z.string()).optional(),
  measureOrder: ExploreMeasureOrder.optional(),
});
export type ExploreSort = z.infer<typeof ExploreSort>;

export const ExploreTimeUnit = z.union([
  ExploreCustomTimeUnit,
  HqlTruncUnitLiteral,
]);
export type ExploreTimeUnit = z.infer<typeof ExploreTimeUnit>;

export const getExploreTimeUnitName = (
  unit: ExploreCustomTimeUnit | string | undefined | null,
  timeUnitConfig: ChartTimeUnitConfig | undefined,
): string | undefined => {
  if (unit == null) {
    return undefined;
  }
  if (zGuard(ExploreCustomTimeUnit, unit)) {
    return unit.name;
  }
  if (unit === "week" && getResolvedWeekStart(timeUnitConfig) === "monday") {
    return "weekmonday";
  }
  return unit;
};

export const ExploreLump = z.object({
  windowFunction: ExploreLumpWindowFunctionLiteral,
  orderBy: ExploreFieldId.optional(),
  orderDirection: ExploreLumpOrderDirectionLiteral.optional(),
  predicate: ExploreRankPredicate,
  otherLabel: z.string().optional(),
});
export type ExploreLump = z.infer<typeof ExploreLump>;

export const ExploreField = z.object({
  id: ExploreFieldId,

  // if empty, then this field belongs to the base dataframe/table/semantic dataset.
  // if referencing a field from a joined table/dataset, then this should be the path to the table
  // e.g. [ "baseTable", "joinedTable" ]
  queryPath: z.array(SemanticDatasetName),
  fieldType: ExploreFieldTypeLiteral,
  value: z.string(),
  seriesId: ExploreSeriesId, //always needed even for the pivot tables
  channel: ExploreChannel,
  dataType: CalciteTypeLiteral,
  title: z.union([z.string(), z.literal(null)]).optional(),
  displayFormat: z
    .union([
      NumberColumnDisplayFormat,
      DateColumnDisplayFormat,
      DatetimeColumnDisplayFormat,
    ])
    .optional(),

  // config for both charts and pivot tables
  aggregation: HqlAggregationFunctionLiteral.optional(), //TODO(EXPLORE): EXP-1221 this should never be set for fieldType == MEASURE
  truncUnit: ExploreTimeUnit.optional(),
  truncUnitConfig: ChartTimeUnitConfig.optional(),
  sort: ExploreSort.optional(), //TODO(EXPLORE) pivot tables should use this
  bin: ExploreBin.optional(), //TODO(EXPLORE) pivot tables should support binning
  lump: ExploreLump.optional(),

  // charts-specific config
  scaleType: ChartDataTypeLiteral.optional(),
  axis: ExploreAxis.optional(),
  colorsBySeriesValues: ChartColorMappings.optional(),
  opacitiesBySeriesValues: ChartOpacitiesBySeriesValues.optional(),

  pivotColumnWidth: z.number().optional(),
});

export type ExploreField = z.infer<typeof ExploreField>;
// Explore field configuration that can be simply adjusted
// without other major structural changes to the related explore spec
export type ExploreFieldSimpleOptions = Omit<
  ExploreField,
  "id" | "value" | "seriesId" | "channel" | "dataType"
>;

export const ExploreViewType = z.enum([
  "source-table", // Nested table only
  "visualization", // Visualization only (Pivot table or chart)
  "both", // In this mode, the visualization is rendered above the nested table
]);
export type ExploreViewType = z.infer<typeof ExploreViewType>;

export const ExploreVisualizationType = z.enum([
  "pivot-table",
  "chart",
  "sankey",
]);
export type ExploreVisualizationType = z.infer<typeof ExploreVisualizationType>;

export type ExploreTypeSelectorOption =
  | ChartTypeSelectorOption
  | "pivot-table"
  | "sankey-column"
  | "sankey-network";

export const ExploreChartSeries = z.object({
  id: ExploreSeriesId,
  type: ChartSeriesTypeLiteral,
  name: z.string().optional(),

  // channels
  color: ExploreColorChannelConfig.optional(),
  opacity: ExploreOpacityChannelConfig.optional(),
  tooltip: ExploreTooltipChannelConfig.optional(),

  // these aren't all channels yet but someday could be
  text: ExploreTextChannelConfig.optional(),
  lineStroke: ExploreLineStrokeChannelConfig.optional(),
  lineWidth: ExploreLineWidthChannelConfig.optional(),
  radius: ExploreRadiusChannelConfig.optional(),
  pointShape: ExplorePointShapeChannelConfig.optional(),
  pointSize: ExplorePointSizeChannelConfig.optional(),

  barGrouped: z.boolean().optional(),

  normalize: z.enum(["facet", "base-axis"]).optional(),

  // applies to line and area
  linePoint: z.boolean().optional(),
  lineShape: ChartInterpolate.optional(),

  // applies to area
  areaLine: z.boolean().optional(),

  //applies to scatter
  pointFilled: z.boolean().optional(),
});
export type ExploreChartSeries = z.infer<typeof ExploreChartSeries>;

export const ExploreChartSettings = ChartGeneralSettings.omit({
  selectionEnabled: true,
  tooltip: true,
});
export type ExploreChartSettings = z.infer<typeof ExploreChartSettings>;

const ExploreChartOrientation = z.enum(["vertical", "horizontal"]);
export type ExploreChartOrientation = z.infer<typeof ExploreChartOrientation>;

export const ExploreChartConfig = z.object({
  series: z.array(ExploreChartSeries),
  seriesGroups: z.array(z.array(ExploreSeriesId)),
  facet: ExploreFacet.optional(),
  settings: ExploreChartSettings,

  // applies to bar today
  orientation: ExploreChartOrientation.optional(),
});
export type ExploreChartConfig = z.infer<typeof ExploreChartConfig>;

export const SankeyTypeLiteral = z.enum(["column", "network"]);
export type SankeyType = z.infer<typeof SankeyTypeLiteral>;
export const SankeyType = getNormalEnum(SankeyTypeLiteral);

export const SankeyNullHandlingLiteral = z.enum([
  "show-all",
  "hide-links",
  "hide-all",
]);
export type SankeyNullHandling = z.infer<typeof SankeyNullHandlingLiteral>;
export const SankeyNullHandling = getNormalEnum(SankeyNullHandlingLiteral);

export const ExploreSankeyConfig = z.object({
  sankeyType: SankeyTypeLiteral,
  /** @deprecated Use nullHandling instead */
  hideNull: z.boolean().optional(),
  nullHandling: SankeyNullHandlingLiteral.optional(),
  hideColumnNames: z.boolean().optional(),
});
export type ExploreSankeyConfig = z.infer<typeof ExploreSankeyConfig>;

export const ExploreUserJoinColumn = z.object({
  name: z.string(),
  dataType: CalciteTypeLiteral,
  /** Present if the related table maps directly to a warehouse table. If a semantic dataset, this won't be defined  */
  dataSourceColumnId: DataSourceColumnId.optional(),
});
export type ExploreUserJoinColumn = z.infer<typeof ExploreUserJoinColumn>;

export const ExploreUserJoinWarehouseTableReference = z.object({
  type: z.literal("warehouse"),
  name: SemanticDatasetName,
});
export const ExploreUserJoinSemanticTableReference = z.object({
  type: z.literal("semantic"),
  name: SemanticDatasetName,
});

/**
 * A union with one member today. It is deliberately *not* collapsed to that
 * member: the exported project file schema emits a titled `$def` per union
 * (docs/EXPORT_README.md), and collapsing it drops
 * `#/$defs/ExploreUserJoinTableReference` from the published spec -- a
 * cosmetic change, but one that would force a schema version bump.
 *
 * `z.union` needs two members at the type level, so the one-element array is
 * cast to a pair. Only the *type* doubles up -- the array passed at runtime has
 * one element, and `z.infer` of a union of one repeated member is that member.
 */
export const ExploreUserJoinTableReference = z.union([
  ExploreUserJoinWarehouseTableReference,
  // Not yet supported, so commenting out for simplicity for now
  // ExploreUserJoinSemanticTableReference,
] as unknown as [
  typeof ExploreUserJoinWarehouseTableReference,
  typeof ExploreUserJoinWarehouseTableReference,
]);

export const ExploreUserJoin = z.object({
  /** Optional name of a join. Needed if there are multiple joins combining the same two tables. */
  joinName: z.string().optional(),

  /** Names of the tables/datasets to join. */
  sourceTable: ExploreUserJoinTableReference,
  targetTable: ExploreUserJoinTableReference,

  sourceTableJoinColumn: ExploreUserJoinColumn,
  targetTableJoinColumn: ExploreUserJoinColumn,

  /**
   * Relative to the base table:
   * - to-one: one-to-one or many-to-one
   * - to-many: one-to-many or many-to-many
   */
  relationshipType: z.enum(["to-one", "to-many"]),
});
export type ExploreUserJoin = z.infer<typeof ExploreUserJoin>;

export const ExploreUserJoinTable = z.object({
  /** Identifier for referencing the table. */
  name: SemanticDatasetName,
  /**
   * Path in the underlying DWH to the table. Should be quoted appropriately.
   * The display name of table can typically be gotten using our
   * `displayNameFromPathToTable()` utility on this value.
   */
  pathToTable: z.string(),
  dataSourceTableId: DataSourceTableId,
  primaryKeyColumn: ExploreUserJoinColumn,
});
export type ExploreUserJoinTable = z.infer<typeof ExploreUserJoinTable>;

export const ExploreDetailField = z.object({
  value: z.string(),
  fieldType: ExploreFieldTypeLiteral,
  dataType: CalciteTypeLiteral,
  queryPath: z.array(SemanticDatasetName),
  // @deprecated
  // kept around only for backwards compatibility with old exported hex projects
  visible: z.boolean().optional(),
});

export type ExploreDetailField = z.infer<typeof ExploreDetailField>;

// We try to keep the fields in here in sync with the upstream data as much as possible.
// This is important for correctness and not erroring, as sql queries generated can depend
// on the fields being available and in sync with the data. This is due to column/type
// information not readily being available in the server where hql queries are generated.
// We'll need to address that problem long-term, but this is a very poor stop-gap for now.
export const ExploreDetails = z.object({
  fields: z.array(ExploreDetailField),
  showAllBaseTableDetailFields: z.boolean(),
  // If false, then all detail fields state is ignored. We have a separate value
  // to track so that we can preserve state when enabling/disabling
  enabled: z.boolean(),
});
export type ExploreDetails = z.infer<typeof ExploreDetails>;

export const ExploreSpec = z.object({
  fields: z.array(ExploreField),
  chartConfig: ExploreChartConfig,
  viewType: ExploreViewType,
  visualizationType: ExploreVisualizationType,
  joins: z.array(ExploreUserJoin).optional(),
  tables: z.array(ExploreUserJoinTable).optional(),
  details: ExploreDetails,

  // TODO: move these into a pivotConfig object once we have a better way to handle pivot table config
  // If true, display multiple values as rows for pivots. Ignored unless there are 2+ values
  valuesAsRows: z.boolean().optional(),
  columnTotals: z.boolean().optional(),
  rowTotals: z.boolean().optional(),
  rowSubtotals: z.boolean().optional(),
  columnSubtotals: z.boolean().optional(),

  // Sankey-specific configuration
  sankeyConfig: ExploreSankeyConfig.optional(),
});
export type ExploreSpec = z.infer<typeof ExploreSpec>;
export interface HqlNextColumn {
  /**
   * column name - named this way for consistency with the `ExploreField`
   */
  value: string;
  /**
   * calc expression - if undefined, then this references a column fron the
   * input. If defined, then this is a column that was added via a calc
   * expression.
   */
  calc?: string;
  fieldType: ExploreFieldType;
  dataType: CalciteType;
  queryPath?: SemanticDatasetName[];
}

/** Override for a field in the explore spec, used by ExploreSpecSessionState */
export const ExploreFieldOverride = z.object({
  sort: ExploreSort.optional(),
});
export type ExploreFieldOverride = z.infer<typeof ExploreFieldOverride>;

/** Collection of field overrides keyed by ExploreFieldId, used in ExploreSpecSessionState */
export const ExploreFieldOverrides = z
  .record(z.string(), ExploreFieldOverride)
  .or(z.null());
export type ExploreFieldOverrides = z.infer<typeof ExploreFieldOverrides>;
